import React, { FunctionComponent, useEffect, useRef } from 'react';
import { T } from 'src/shared/helpers';
import SearchIcon from 'src/shared/material_icons/action/search.svg';
import styled from 'styled-components';
import { useAgentAssistant } from '../agentAssistant/AgentAssistantContext';
import { useDialogManager } from '../dialog';
import { Icon } from '../icon';
import QuickSearchDialog from '../quickSearch/quickSearchDialog';

// Single Search & Help entry point for the sidebar. The button opens the quick search dialog (Ctrl+K).

const Button = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 7px 12px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 6px;
    font-size: 12px;
    font-family: inherit;
    color: #333;
    background: white;
    box-sizing: border-box;
    cursor: pointer;
    transition:
        border-color 0.15s ease,
        background 0.15s ease;

    &:hover {
        border-color: rgba(0, 0, 0, 0.25);
        background: rgba(0, 0, 0, 0.03);
    }
`;

const Label = styled.span`
    flex: 1;
    text-align: left;
    color: #999;
`;

const Kbd = styled.span`
    font-size: 9px;
    font-weight: 600;
    color: #888;
    background: rgba(0, 0, 0, 0.06);
    padding: 2px 6px;
    border-radius: 4px;
    pointer-events: none;
    letter-spacing: 0.3px;
`;

const SidebarSearchHelp: FunctionComponent = () => {
    const { showDialog } = useDialogManager();
    const { isEnabled: isAgentEnabled, isOpen: isAgentOpen, isFullScreen: isAgentFullScreen, close: closeAgent, openSidebarWithPrompt } = useAgentAssistant();
    // The dialogs render outside AgentAssistantProvider, so the AI Mode entry is handed over as a callback. The Ctrl+K
    // listener is registered once, hence the ref: it always reads the current agent state when the dialog opens.
    const agentRef = useRef({ isAgentEnabled, isSidebarOpen: isAgentOpen && !isAgentFullScreen, closeAgent, openSidebarWithPrompt });
    agentRef.current = { isAgentEnabled, isSidebarOpen: isAgentOpen && !isAgentFullScreen, closeAgent, openSidebarWithPrompt };

    const openSearch = () => {
        const { isAgentEnabled: canAskAiMode, isSidebarOpen, closeAgent: closeSidebar, openSidebarWithPrompt: askAiMode } = agentRef.current;
        // The search dialog takes over: an open AI Mode sidebar steps aside.
        if (isSidebarOpen) closeSidebar();
        showDialog('quick-search', <QuickSearchDialog onAskAiMode={canAskAiMode ? askAiMode : undefined} />);
    };

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.ctrlKey && e.key === 'k') {
                e.preventDefault();
                e.stopImmediatePropagation();
                openSearch();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Button type='button' onClick={openSearch} aria-label={T._('Search & Help', 'Admin')}>
            <Icon>
                <SearchIcon />
            </Icon>
            <Label>{T._('Search & help...', 'Admin')}</Label>
            <Kbd>CTRL+K</Kbd>
        </Button>
    );
};

export default SidebarSearchHelp;
